import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { logError } from 'commons/logger';
import Link from 'components/consumer/Link';
import useScreen from 'hooks/useScreen';
import JBImage from 'components/essentials/JBImage';
import CtaButton from 'components/essentials/Cta/CtaButton';
import SanitizedHtml from 'components/consumer/SanitizedHtml';
import IconPlus from 'components/icons/iconPlus';
import ProgressLoaderDots from 'components/essentials/ProgressLoaderDots/ProgressLoaderDots';
import PDPUpSellFlyOut from './PDPUpSellFlyOut';
import PDPPriceSectionUI from '../PDPPriceSection/PDPPriceSectionUI';
const PDPUpSellProductUI = ({
  getCart,
  addToCart,
  optionPrice,
  priceDetails,
  upSellImgUrl,
  upSellProduct,
  addingToCart,
  upSellImgFallbackUrl,
  addUpSellProductEvent,
  upSellProductLink,
  parentProductSelectedOptions,
  isLayoutRevamp = false
}) => {
  // state
  const [flyOutTop, setFlyOutTop] = useState(0);
  const [isInfoHovered, setIsInfoHovered] = useState(false);

  // refs
  const containerRef = useRef(null);

  // functions
  const toggleFlyOut = isVisible => {
    setIsInfoHovered(isVisible);
    setFlyOutTop(containerRef.current?.getBoundingClientRect().top || 0);
  };

  // media queries
  const {
    isDesktop
  } = useScreen();
  const product = upSellProduct?.product;
  if (!product) return null;
  const onAddClick = async () => {
    try {
      await addToCart({
        productId: product.id,
        selectedOptions: product.options ? product.options.filter(o => !!o.default_value).reduce((acc, cur) => {
          acc[cur.id] = parentProductSelectedOptions?.[cur.id] ?? cur.default_value;
          return acc;
        }, {}) : {}
      });
      addUpSellProductEvent(product, optionPrice);
      getCart();
    } catch (e) {
      logError(e, 'add to cart failed');
    }
  };
  const flyOut = isDesktop && isInfoHovered && <PDPUpSellFlyOut top={flyOutTop} name={product.name} optionPrice={optionPrice} price={product.meta.price} salePrice={product.meta.price.final_price} promotion={product.meta.promotion} description={upSellProduct.upsell_flyout_description} totalReviews={upSellProduct.product_total_reviews} averageRating={upSellProduct.product_average_rating} imageUrl={upSellImgUrl || upSellProduct.upsell_flyout_image || product.hero_images_min?.[0]?.url} />;
  const header = upSellProduct.upsell_header && <h3 className="m-0 text-base font-bold">
      <SanitizedHtml as="span" html={upSellProduct.upsell_header} />
    </h3>;
  const priceSection = <PDPPriceSectionUI price={priceDetails?.priceWithOptionsPrice} salePrice={priceDetails?.salePriceWithOptionsPrice} leastTieredDiscountedPrice={priceDetails?.leastTieredDiscountedPrice} maxTieredDiscountedPrice={priceDetails?.maxTieredDiscountedPrice} hasDiscount={priceDetails?.hasDiscount} hasTieredDiscount={priceDetails?.hasTieredDiscount} variant="PDPUpSellProduct" />;

  // Half width from md matches the mock's paired cards
  if (isLayoutRevamp) {
    return <div className="mt-8 mx-0 mb-0 lg:z-[2] lg:relative" ref={containerRef} data-xc="PDPUpSellProductRevamp">
        {flyOut}
        {header}

        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div className="flex flex-col border border-solid border-gray-light6">
            <Link to={upSellProductLink} className="flex flex-1 flex-col p-3 text-gray no-underline hover:no-underline" onMouseEnter={() => toggleFlyOut(true)} onMouseLeave={() => toggleFlyOut(false)} onFocus={() => toggleFlyOut(true)} onBlur={() => toggleFlyOut(false)}>
              {upSellImgUrl && <div className="flex h-32 items-center justify-center">
                  <JBImage width={200} src={upSellImgUrl} className="max-h-full object-contain" fallbackSrc={upSellImgFallbackUrl} />
                </div>}
              <div className="mt-3 text-sm font-bold">{product.name}</div>
              <div className="PDPUpSellProduct__product-price">
                {priceSection}
              </div>
            </Link>

            <button type="button" data-xc="add-upsell-button"
          // The main CTA is also named "Add to Bag"
          aria-label={`Add ${product.name} to Bag`} className="flex w-full items-center justify-between border-0 border-t border-solid border-gray-light6 bg-transparent px-3 py-2 text-brand hover:cursor-pointer hover:underline disabled:cursor-default" disabled={addingToCart} onClick={onAddClick}>
              <span>Add to Bag</span>
              {addingToCart ? <ProgressLoaderDots inline grey className="m-0" /> : <IconPlus color="currentColor" width={16} height={16} />}
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="mt-8 mx-0 mb-0 lg:z-[2] lg:relative" ref={containerRef}>
      {flyOut}
      {header}

      <div className="flex mt-2 border border-solid border-gray-light6 rounded">
        {upSellImgUrl && <Link to={upSellProductLink} className="p-4 flex-[0.43] lg:flex-[0.4] cursor-pointer">
            {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
            <div className="flex flex-col justify-center items-center h-full bg-white" onMouseOver={() => toggleFlyOut(true)} onMouseOut={() => toggleFlyOut(false)}>
              <JBImage width={200} src={upSellImgUrl} className="h-full object-contain" fallbackSrc={upSellImgFallbackUrl} />
            </div>
          </Link>}

        <div className="p-4 flex-[0.57] lg:flex-[0.6]">
          <Link to={upSellProductLink}>
            <div className="text-sm font-bold">{product.name}</div>
          </Link>
          <div className="PDPUpSellProduct__product-price">{priceSection}</div>

          <SanitizedHtml className="text-xs mt-2 mx-0 mb-3 text-gray-light1" html={upSellProduct.upsell_description} />

          <CtaButton block type="submit" color="brand-light" styling="solid-button" data-xc="add-upsell-button" className="w-auto min-w-[80px] p-[.3rem] md:px-3 md:py-[.3rem] hover:bg-brand-light" disabled={addingToCart} onClick={onAddClick} isLoading={addingToCart}>
            + Add
          </CtaButton>
        </div>
      </div>
    </div>;
};
PDPUpSellProductUI.propTypes = {
  getCart: PropTypes.func,
  optionPrice: PropTypes.number,
  priceDetails: PropTypes.shape({
    priceWithOptionsPrice: PropTypes.number,
    salePriceWithOptionsPrice: PropTypes.number,
    leastTieredDiscountedPrice: PropTypes.number,
    maxTieredDiscountedPrice: PropTypes.number,
    hasDiscount: PropTypes.bool,
    hasTieredDiscount: PropTypes.bool
  }),
  upSellImgUrl: PropTypes.string,
  upSellProduct: PropTypes.shape({
    upsell_flyout_description: PropTypes.string,
    upsell_flyout_image: PropTypes.string,
    upsell_header: PropTypes.string,
    upsell_description: PropTypes.string,
    product_total_reviews: PropTypes.number,
    product_average_rating: PropTypes.number,
    product: PropTypes.object,
    product_id: PropTypes.number
  }),
  addToCart: PropTypes.func,
  addingToCart: PropTypes.bool,
  addUpSellProductEvent: PropTypes.func,
  upSellImgFallbackUrl: PropTypes.string,
  upSellProductLink: PropTypes.string,
  parentProductSelectedOptions: PropTypes.object,
  isLayoutRevamp: PropTypes.bool
};
export default PDPUpSellProductUI;